/**
 * Turn automatic renewal back on, or — when the Stripe subscription is gone —
 * start a new checkout for the host's own plan.
 *
 * Outcomes from the reactivate-subscription Edge Function:
 *   - { reactivated: true }            — renewal is back on, nothing charged.
 *   - { needs_checkout: true, tier, tier_id, interval } — the subscription
 *     ended; we return the plan to buy (same tier / interval) and the caller
 *     opens PlanChangeFlow (price shown + confirm) — never straight to Stripe.
 *   - { error_code: PAYMENT_ISSUE | ACCOUNT_PENDING_DELETION, error } —
 *     thrown as BillingError.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthReady } from '@/hooks/useAuthReady';
import { getSupabase } from '@/lib/supabase';
import { readFunctionError, BillingError } from '@/lib/billingErrors';
import { tierKeyFromPlan } from '@/lib/billingAccess';
import type { PricingTierId } from '@/lib/stripe';
import { useBillingInfo, subscriptionKeys } from './useSubscription';

export interface ReactivateResult {
  reactivated?: boolean;
  needsCheckout?: boolean;
  /** What to buy when needsCheckout (open PlanChangeFlow with it). */
  checkoutRequest?: { tier: PricingTierId; interval: 'month' | 'year' };
}

export function useReactivateSubscription() {
  const { userId } = useAuthReady();
  const queryClient = useQueryClient();
  const { data: billing } = useBillingInfo();

  return useMutation({
    mutationFn: async (): Promise<ReactivateResult> => {
      if (!userId) throw new BillingError('SESSION');

      const { data, error } = await getSupabase().functions.invoke('reactivate-subscription', {
        body: {},
      });

      const info = await readFunctionError(data, error);
      if (info) throw new BillingError(info.code ?? 'SERVER', info);

      const body = (data ?? {}) as {
        reactivated?: boolean;
        needs_checkout?: boolean;
        tier?: string | null;
        tier_id?: string | null;
        interval?: string | null;
      };

      // The Stripe subscription is gone → new checkout for the host's own tier.
      if (body.needs_checkout) {
        const tier =
          tierKeyFromPlan(body.tier) ??
          tierKeyFromPlan(body.tier_id) ??
          tierKeyFromPlan(billing?.subscription_plan) ??
          'starter';
        const wanted = body.interval ?? billing?.billing_interval;
        // Early Adopter is sold yearly only.
        const interval: 'month' | 'year' =
          tier === 'earlyAdopter' ? 'year' : wanted === 'month' ? 'month' : 'year';
        return { needsCheckout: true, checkoutRequest: { tier, interval } };
      }

      return { reactivated: body.reactivated === true };
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.billing(userId) });
      }
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}
